import * as React from 'react';
import { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CssBaseline,
  FormControl,
  FormLabel,
  TextField,
  Typography,
  Stack,
  Card as MuiCard,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Store, Person, Lock, RestaurantMenu } from '@mui/icons-material';

// === GLASSMORPHIC CARD ===
const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  maxWidth: '960px',
  padding: theme.spacing(3),
  borderRadius: '16px',
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(212, 175, 55, 0.2)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
  opacity: 0,
  transform: 'translateY(20px)',
  '&.loaded': {
    opacity: 1,
    transform: 'translateY(0)',
  },
  '&:hover': {
    transform: 'translateY(-4px) scale(1.005)',
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.18)',
  },
}));

const SignUpContainer = styled(Stack)(({ theme }) => ({
  minHeight: '100vh',
  padding: theme.spacing(3),
  background: 'linear-gradient(135deg, #f8f8f8 0%, #f0f0f0 100%)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const AdContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(5px)',
  borderLeft: '1px solid rgba(212, 175, 55, 0.2)',
  borderRadius: '0 16px 16px 0',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    fontSize: '0.95rem',
    height: '42px',
    fontFamily: "'Poppins', sans-serif",
    color: '#1a1a1a',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 0.4)',
      boxShadow: `0 0 0 2px rgba(212, 175, 55, 0.3)`,
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.95rem',
    color: '#555555',
    transform: 'translate(14px, 11px) scale(1)',
    fontFamily: "'Poppins', sans-serif",
    transition: 'all 0.2s ease',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  fontSize: '0.95rem',
  padding: theme.spacing(1, 3.5),
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  fontFamily: "'Poppins', sans-serif",
  background: 'linear-gradient(to right, #d4af37, #b8932b)',
  color: '#fff',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.22)',
    transform: 'translateY(-2px)',
    background: 'linear-gradient(to right, #c19a2a, #a88323)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  fontSize: '0.95rem',
  fontFamily: "'Poppins', sans-serif'",
  backgroundColor: 'rgba(254, 242, 242, 0.8)',
  color: '#d32f2f',
}));

const SuccessAlert = styled(Alert)(({ theme }) => ({
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  fontSize: '0.95rem',
  fontFamily: "'Poppins', sans-serif'",
  backgroundColor: 'rgba(240, 253, 244, 0.9)',
  color: '#28a745',
}));

const Logo = styled(Typography)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  fontSize: '2.8rem',
  background: 'linear-gradient(to right, #d4af37, #b8932b)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  letterSpacing: '1.5px',
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
}));

export default function AdminSignup() {
  const [form, setForm] = useState({ restaurantName: '', username: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cardLoaded, setCardLoaded] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const timer = setTimeout(() => setCardLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/admin-signup.php`, form);
      setSuccess('Account created! Redirecting to login...');
      setTimeout(() => navigate('/admin/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Signup failed');
    }
  };

  return (
    <SignUpContainer direction="column" justifyContent="center" alignItems="center">
      <CssBaseline />
      
      <Card variant="outlined" className={cardLoaded ? 'loaded' : ''}>
        <FormContainer>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography
              component="h2"
              variant="h5"
              sx={{
                fontFamily: "'Poppins', sans-serif",
                color: '#1a1a1a',
                fontWeight: 600,
                fontSize: '1.8rem',
              }}
            >
              Register Restaurant
            </Typography>
            <RestaurantMenu sx={{ color: '#d4af37', fontSize: 32, opacity: 0.9 }} />
          </Box>

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && (
              <StyledAlert severity="error">
                {error}
              </StyledAlert>
            )}
            {success && (
              <SuccessAlert severity="success">
                {success}
              </SuccessAlert>
            )}

            <FormControl>
              <FormLabel sx={{ fontFamily: "'Poppins', sans-serif", color: '#1a1a1a', fontSize: '0.95rem', fontWeight: 600 }}>
                Restaurant Name
              </FormLabel>
              <StyledTextField
                name="restaurantName"
                placeholder="e.g., The Golden Spoon"
                value={form.restaurantName}
                onChange={(e) => setForm({ ...form, restaurantName: e.target.value })}
                required
                fullWidth
                variant="outlined"
                InputProps={{
                  startAdornment: <Store sx={{ color: '#d4af37', mr: 1, fontSize: 20 }} />,
                }}
              />
            </FormControl>

            <FormControl>
              <FormLabel sx={{ fontFamily: "'Poppins', sans-serif", color: '#1a1a1a', fontSize: '0.95rem', fontWeight: 600 }}>
                Username
              </FormLabel>
              <StyledTextField
                name="username"
                placeholder="admin@restaurant.com"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                required
                fullWidth
                variant="outlined"
                InputProps={{
                  startAdornment: <Person sx={{ color: '#d4af37', mr: 1, fontSize: 20 }} />,
                }}
              />
            </FormControl>

            <FormControl>
              <FormLabel sx={{ fontFamily: "'Poppins', sans-serif", color: '#1a1a1a', fontSize: '0.95rem', fontWeight: 600 }}>
                Password
              </FormLabel>
              <StyledTextField
                name="password"
                placeholder="••••••••"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                fullWidth
                variant="outlined"
                InputProps={{
                  startAdornment: <Lock sx={{ color: '#d4af37', mr: 1, fontSize: 20 }} />,
                }}
              />
            </FormControl>

            <FormControl>
              <FormLabel sx={{ fontFamily: "'Poppins', sans-serif", color: '#1a1a1a', fontSize: '0.95rem', fontWeight: 600 }}>
                Confirm Password
              </FormLabel>
              <StyledTextField
                name="confirmPassword"
                placeholder="••••••••"
                type="password"
                value={form.confirmPassword}
                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                required
                fullWidth
                variant="outlined"
                InputProps={{
                  startAdornment: <Lock sx={{ color: '#d4af37', mr: 1, fontSize: 20 }} />,
                }}
              />
            </FormControl>

            <StyledButton type="submit" fullWidth variant="contained">
              Sign Up
            </StyledButton>

            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                fontFamily: "'Poppins', sans-serif",
                color: '#1a1a1a',
                mt: 1,
              }}
            >
              Have an account?{' '}
              <Link
                to="/admin/login"
                style={{
                  color: '#d4af37',
                  textDecoration: 'none',
                  fontWeight: 600,
                  transition: 'color 0.2s',
                }}
              >
                Login
              </Link>
            </Typography>
          </Box>
        </FormContainer>

        <AdContainer>
          <Logo variant="h4">RasoiDesk</Logo>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "'Poppins', sans-serif",
              color: '#1a1a1a',
              textAlign: 'center',
              opacity: 0.8,
              mb: 3,
              lineHeight: 1.6,
            }}
          >
            Start your journey with elegance.
            <br />
            Register and manage with confidence.
          </Typography>
          <Box
            sx={{
              width: '180px',
              height: '180px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
              borderRadius: '16px',
              overflow: 'hidden',
              bgcolor: '#fff',
              p: 1,
            }}
          >
            <img
              src="../RasoiDesk.jpeg"
              alt="RestroHub Logo"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </Box>
        </AdContainer>
      </Card>
    </SignUpContainer>
  );
}